import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ORDER_TYPE_RESTAURANT } from '../orderManagementConfig';
import { getRegionalBranchForOrdersApi } from '../orderManagementUtils';
import { apiOrdersToTrackingOrders, getMapPlottableOrders } from './mapOrderTrackingModel';
import { MAP_MARKER_VIEW } from './constants';
import { OrderTrackingMapToolbar } from './OrderTrackingMapToolbar';
import { OrdersTrackingPanel } from './OrdersTrackingPanel';
import { TrackingMap } from './TrackingMap';
import './OrderTrackingDashboard.scss';

export function OrderTrackingDashboard({ orders }) {
  const location = useLocation();
  const hydratedNavState = useRef(false);

  const [selectedId, setSelectedId] = useState(null);
  const [mapStyle, setMapStyle] = useState('roadmap');
  const [mapMarkerView, setMapMarkerView] = useState(MAP_MARKER_VIEW.RESTAURANT);
  const [mapRef, setMapRef] = useState(null);

  useEffect(() => {
    if (hydratedNavState.current) return;
    const s = location.state;
    if (!s) return;
    hydratedNavState.current = true;
    if (s.deliveryDate) orders.setDeliveryDate(s.deliveryDate);
    if (s.statusFilter) orders.setStatusFilter(s.statusFilter);
    if (s.orderIdSearch != null) orders.setOrderIdSearch(s.orderIdSearch);
    if (s.deliveryTypeFilter) orders.setDeliveryTypeFilter(s.deliveryTypeFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackingOrders = useMemo(
    () => apiOrdersToTrackingOrders(orders.filteredOrders),
    [orders.filteredOrders]
  );

  const listOrders = trackingOrders;

  const mapOrders = getMapPlottableOrders(listOrders);

  const ordersWithoutMap = listOrders.length - mapOrders.length;

  const selectedOrder = useMemo(() => {
    if (!selectedId) return null;
    return listOrders.find((o) => o.id === selectedId) ?? null;
  }, [listOrders, selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    if (!listOrders.some((o) => o.id === selectedId)) setSelectedId(null);
  }, [listOrders, selectedId]);

  useEffect(() => {
    orders.setStatusFilter('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders.deliveryDate]);

  const handleSelectOrder = (id) => {
    setSelectedId((prev) => (prev === id ? null : id));
  };

  const handleMapMarkerViewChange = (view) => {
    setMapMarkerView(view);
    setSelectedId(null);
  };

  const filterState = useMemo(
    () => ({
      orderTypeView: ORDER_TYPE_RESTAURANT,
      deliveryDate: orders.deliveryDate,
      isNextDayPromo: false,
      nextDayPromoDateLabel: orders.nextDayPromoDateLabel,
      statusFilter: orders.statusFilter,
      deliveryTypeFilter: orders.deliveryTypeFilter,
      orderIdSearch: orders.orderIdSearch,
      nextDayPromoDayFilter: orders.nextDayPromoDayFilter,
    }),
    [
      orders.deliveryDate,
      orders.nextDayPromoDateLabel,
      orders.statusFilter,
      orders.deliveryTypeFilter,
      orders.orderIdSearch,
      orders.nextDayPromoDayFilter,
    ]
  );

  const lastRefreshLabel = orders.formatLastRefresh(orders.lastRefreshAt);

  return (
    <div className='otd-layout'>
      <OrderTrackingMapToolbar
        loading={orders.loading}
        onRefresh={orders.refresh}
        deliveryDate={orders.deliveryDate}
        onDeliveryDateChange={orders.setDeliveryDate}
        statusFilter={orders.statusFilter}
        onStatusFilterChange={orders.setStatusFilter}
        statusTabCounts={orders.tabCounts}
        deliveryTypeFilter={orders.deliveryTypeFilter}
        onDeliveryTypeFilterChange={orders.setDeliveryTypeFilter}
        orderIdSearch={orders.orderIdSearch}
        onOrderIdSearchChange={orders.setOrderIdSearch}
        filterState={filterState}
        lastRefreshLabel={lastRefreshLabel}
        mapMarkerView={mapMarkerView}
        onMapMarkerViewChange={handleMapMarkerViewChange}
      />

      <div className='otd-layout__body'>
        <OrdersTrackingPanel
          regionalBranch={getRegionalBranchForOrdersApi()}
          loading={orders.loading}
          loadError={orders.loadError}
          filteredCount={orders.filteredOrders.length}
          totalCount={orders.totalCount}
          listCount={listOrders.length}
          mapCount={mapOrders.length}
          ordersWithoutMap={ordersWithoutMap}
          orders={listOrders}
          selectedId={selectedId}
          onSelectOrder={handleSelectOrder}
        />
        <TrackingMap
          orders={mapOrders}
          selectedOrder={selectedOrder}
          selectedId={selectedId}
          mapMarkerView={mapMarkerView}
          mapStyle={mapStyle}
          onMapStyleChange={setMapStyle}
          onCloseTimeline={() => setSelectedId(null)}
          onSelectOrder={handleSelectOrder}
          mapRef={mapRef}
          setMapRef={setMapRef}
        />
      </div>
    </div>
  );
}
